import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const DESTRUCTIVE = ["purge", "delete"];

export default function BulkDeleteDatabasesModal({
  targets,
  onArchive,
  onRestore,
  onPurge,
  onDelete,
  onCancel,
  busy,
}) {
  const { t } = useTranslation();

  const [pendingAction, setPendingAction] = useState(null);
  const [typedPhrase, setTypedPhrase] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) {
        if (pendingAction) { setPendingAction(null); setTypedPhrase(""); }
        else onCancel();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel, pendingAction, busy]);

  useEffect(() => {
    setPendingAction(null);
    setTypedPhrase("");
  }, [targets]);

  useEffect(() => {
    if (pendingAction && DESTRUCTIVE.includes(pendingAction) && inputRef.current) {
      inputRef.current.focus();
    }
  }, [pendingAction]);

  const count = targets?.length || 0;
  const archivedCount = useMemo(
    () => (targets || []).filter((tg) => tg.isClosed).length,
    [targets]
  );
  const allArchived = count > 0 && archivedCount === count;
  const allActive = count > 0 && archivedCount === 0;
  const mixed = !allArchived && !allActive;

  if (!targets || count === 0) return null;

  const requiredPhrase = `DELETE ${count} DATABASE${count === 1 ? "" : "S"}`;
  const phraseMatches = typedPhrase.trim().toUpperCase() === requiredPhrase;

  const nameList = (
    <ul className="bulkModalNameList">
      {targets.map((tg) => (
        <li key={String(tg.id)} title={tg.label}>{tg.label}</li>
      ))}
    </ul>
  );

  if (pendingAction && DESTRUCTIVE.includes(pendingAction)) {
    const isPurge = pendingAction === "purge";

    const actionLabel = isPurge
      ? t("admin.bulkPurgeAction", { count, defaultValue: `Purge data in ${count} database(s)` })
      : t("admin.bulkDeleteAction", { count, defaultValue: `Delete ${count} database(s) permanently` });

    const actionDesc = isPurge
      ? t("admin.bulkPurgeConfirmDesc", {
          count,
          defaultValue:
            "All records in every selected database will be deleted. Schemas, indexes and settings are kept. This cannot be undone.",
        })
      : t("admin.bulkDeleteConfirmDesc", {
          count,
          defaultValue:
            "Every selected database table and all records inside will be permanently dropped. This cannot be undone.",
        });

    const goBack = () => { if (busy) return; setPendingAction(null); setTypedPhrase(""); };
    const confirm = () => {
      setPendingAction(null);
      setTypedPhrase("");
      if (isPurge) onPurge();
      else onDelete();
    };

    return (
      <div className="dbModalOverlay" onClick={goBack}>
        <div className="dbModalCard dbModalConfirmCard" onClick={(e) => e.stopPropagation()}>
          <button
            className="drawer-close-btn drawer-close-btn-abs"
            onClick={goBack}
            disabled={busy}
            aria-label="Close"
            title="Close"
          >
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
              <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
              <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          </button>

          <div className={`dbModalConfirmBadge ${isPurge ? "dbModalConfirmBadge--warn" : "dbModalConfirmBadge--danger"}`}>
            {isPurge ? "⚠" : "🗑"}
          </div>

          <div className="dbModalTitle">
            {t("admin.confirmActionTitle", { defaultValue: "Confirm action" })}
          </div>

          <div className="dbModalDbName">
            {t("admin.bulkSelectedCount", { count, defaultValue: `${count} database(s) selected` })}
          </div>

          {nameList}

          <div className={`dbModalConfirmBanner ${isPurge ? "dbModalConfirmBanner--warn" : "dbModalConfirmBanner--danger"}`}>
            <strong>{actionLabel}</strong>
            <p>{actionDesc}</p>
          </div>

          <div className="dbModalConfirmInputGroup">
            <label className="dbModalConfirmLabel">
              {t("admin.bulkTypePhraseToConfirm", {
                defaultValue: "Type the phrase to confirm:",
              })}
              <span className="dbModalConfirmLabelName">{requiredPhrase}</span>
            </label>
            <input
              ref={inputRef}
              className={`dbModalConfirmInput${typedPhrase && !phraseMatches ? " dbModalConfirmInput--invalid" : ""}${phraseMatches ? " dbModalConfirmInput--valid" : ""}`}
              type="text"
              value={typedPhrase}
              onChange={(e) => setTypedPhrase(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && phraseMatches && !busy) confirm(); }}
              placeholder={requiredPhrase}
              autoComplete="off"
              spellCheck={false}
              disabled={busy}
              aria-label={`Type ${requiredPhrase} to confirm`}
            />
            {typedPhrase && !phraseMatches && (
              <span className="dbModalConfirmInputHint">
                {t("admin.phraseMismatch", { defaultValue: "Phrase does not match." })}
              </span>
            )}
          </div>

          <div className="dbModalConfirmActions">
            <button type="button" className="dbModalCancel dbModalConfirmBack" onClick={goBack} disabled={busy}>
              ← {t("admin.back", { defaultValue: "Go back" })}
            </button>
            <button
              type="button"
              className={`dbModalConfirmBtn ${isPurge ? "dbModalConfirmBtn--warn" : "dbModalConfirmBtn--danger"}`}
              disabled={!phraseMatches || busy}
              onClick={confirm}
            >
              {busy
                ? t("admin.bulkWorking", { defaultValue: "Working…" })
                : actionLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (pendingAction === "archive" || pendingAction === "restore") {
    const isRestore = pendingAction === "restore";
    const goBack = () => { if (busy) return; setPendingAction(null); };
    const confirm = () => {
      setPendingAction(null);
      if (isRestore) onRestore();
      else onArchive();
    };
    const archiveLabel = isRestore
      ? t("admin.bulkRestoreAction", { count, defaultValue: `Restore ${count} database(s)` })
      : t("admin.bulkArchiveAction", { count, defaultValue: `Archive ${count} database(s)` });
    const archiveDesc = isRestore
      ? t("admin.bulkRestoreConfirmDesc", {
          defaultValue: "The selected databases will become visible to users again. No data is changed.",
        })
      : t("admin.bulkArchiveConfirmDesc", {
          defaultValue: "The selected databases will be hidden from users. All data is preserved and can be restored at any time.",
        });

    return (
      <div className="dbModalOverlay" onClick={goBack}>
        <div className="dbModalCard dbModalConfirmCard" onClick={(e) => e.stopPropagation()}>
          <button
            className="drawer-close-btn drawer-close-btn-abs"
            onClick={goBack}
            disabled={busy}
            aria-label="Close"
            title="Close"
          >
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
              <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
              <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          </button>

          <div className="dbModalConfirmBadge dbModalConfirmBadge--info">
            {isRestore ? "↩" : "⊘"}
          </div>

          <div className="dbModalTitle">
            {t("admin.confirmActionTitle", { defaultValue: "Confirm action" })}
          </div>

          <div className="dbModalDbName">
            {t("admin.bulkSelectedCount", { count, defaultValue: `${count} database(s) selected` })}
          </div>

          {nameList}

          <div className="dbModalConfirmBanner dbModalConfirmBanner--info">
            <strong>{archiveLabel}</strong>
            <p>{archiveDesc}</p>
          </div>

          <div className="dbModalConfirmActions">
            <button type="button" className="dbModalCancel dbModalConfirmBack" onClick={goBack} disabled={busy}>
              ← {t("admin.back", { defaultValue: "Go back" })}
            </button>
            <button
              type="button"
              className="dbModalConfirmBtn dbModalConfirmBtn--info"
              onClick={confirm}
              disabled={busy}
            >
              {busy
                ? t("admin.bulkWorking", { defaultValue: "Working…" })
                : archiveLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 1 — choose action
  const archiveDisabledReason = mixed
    ? t("admin.bulkArchiveMixedDisabled", {
        defaultValue: "Selection mixes active and archived databases. Choose one type to archive or restore.",
      })
    : "";

  return (
    <div className="dbModalOverlay" onClick={onCancel}>
      <div className="dbModalCard" onClick={(e) => e.stopPropagation()}>
        <button
          className="drawer-close-btn drawer-close-btn-abs"
          onClick={onCancel}
          aria-label="Close"
          title="Close"
        >
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
            <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
        </button>

        <div className="dbModalIcon">⚠</div>

        <div className="dbModalTitle">
          {t("admin.bulkRemoveTitle", { defaultValue: "Remove multiple databases" })}
        </div>

        <div className="dbModalDbName">
          {t("admin.bulkSelectedCount", { count, defaultValue: `${count} database(s) selected` })}
        </div>

        {nameList}

        <div className="dbModalDesc">
          {t("admin.bulkRemoveDesc", { defaultValue: "How would you like to remove the selected databases?" })}
        </div>

        <div className="dbModalOptions">
          <button
            type="button"
            className="dbModalOptionBtn dbModalOptionArchive"
            disabled={mixed}
            title={archiveDisabledReason || undefined}
            onClick={() => setPendingAction(allArchived ? "restore" : "archive")}
          >
            <span className="dbModalOptionIcon">{allArchived ? "↩" : "⊘"}</span>
            <span className="dbModalOptionBody">
              <span className="dbModalOptionTitle">
                {allArchived
                  ? t("admin.restoreOption", { defaultValue: "Restore" })
                  : t("admin.archiveOption", { defaultValue: "Archive" })}
                {!allArchived && !mixed && (
                  <span className="dbModalRecommended">
                    {t("admin.recommended", { defaultValue: "Recommended" })}
                  </span>
                )}
              </span>
              <span className="dbModalOptionDesc">
                {mixed
                  ? archiveDisabledReason
                  : allArchived
                  ? t("admin.restoreOptionDesc", {
                      defaultValue: "Makes the database accessible to users again. No data is changed.",
                    })
                  : t("admin.archiveOptionDesc", {
                      defaultValue: "Hides the database from users. All data is preserved and can be restored.",
                    })}
              </span>
            </span>
          </button>

          <button
            type="button"
            className="dbModalOptionBtn dbModalOptionPurge"
            onClick={() => setPendingAction("purge")}
          >
            <span className="dbModalOptionIcon">⌫</span>
            <span className="dbModalOptionBody">
              <span className="dbModalOptionTitle">
                {t("admin.purgeOption", { defaultValue: "Purge data" })}
              </span>
              <span className="dbModalOptionDesc">
                {t("admin.purgeOptionDesc", {
                  defaultValue: "Deletes all records from the table. Schema, indexes and settings are kept intact.",
                })}
              </span>
            </span>
          </button>

          <button
            type="button"
            className="dbModalOptionBtn dbModalOptionDelete"
            onClick={() => setPendingAction("delete")}
          >
            <span className="dbModalOptionIcon">×</span>
            <span className="dbModalOptionBody">
              <span className="dbModalOptionTitle">
                {t("admin.deleteOption", { defaultValue: "Delete permanently" })}
              </span>
              <span className="dbModalOptionDesc">
                {t("admin.deleteOptionDesc", {
                  defaultValue: "Drops the table and removes all data. This cannot be undone.",
                })}
              </span>
            </span>
          </button>
        </div>

        <button type="button" className="dbModalCancel" onClick={onCancel}>
          {t("admin.cancel", { defaultValue: "Cancel" })}
        </button>
      </div>
    </div>
  );
}
